import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/useEntity';
import { fmt } from '../lib/formatters';
import { useAuth } from '../lib/useAuth';
import { useClienteActivo } from '../lib/cliente-activo';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';

/**
 * Inicio del rediseño UX (fase 5): cues del cliente activo (cada número
 * abre su lista, patrón Business Central), actividad reciente y tendencia
 * de ventas. Sustituye al Home genérico y a los dashboards vacíos de
 * GAME / TICNOVA. Datos: GET /api/inicio?cliente=…
 */

type Inicio = {
  cliente: string;
  catalogo: { vigentes: number; fabricacion: number; revisar: number; total: number };
  cues: {
    rotura: { n: number; sin_stock: number; umbral: number; unidad: 'meses' | 'semanas' };
    pedidos: { abiertos: number; importe: number; por_estado: { estado: string; n: number }[]; pendientes_respuesta: number };
    cotizaciones: { sin_respuesta: number; sin_enviar: number; mas_antigua: string | null; dias_mas_antigua: number | null };
    revisar: number;
    ventas_approx: { hasta: string | null; dias: number | null; aviso_dias: number };
    coste_entrada: { hasta: string | null; dias: number | null; aviso_dias: number } | null;
    stock: { hasta: string | null; dias: number | null };
  };
  serie: { unidad: 'mes' | 'semana'; puntos: { periodo: string; unidades: number; provisional: boolean }[] };
  top: { id: string; sku: string; nombre: string; unidades: number; periodo: string }[];
  actividad: { tipo: 'importacion' | 'cotizacion' | 'pedido' | 'ficha'; titulo: string; detalle: string | null; fecha: string; to: string | null }[];
};

const ESTADO_LABEL: Record<string, string> = { PDTE_RESPUESTA: 'pdte. respuesta', ACEPTADO: 'aceptados', EN_PRODUCCION: 'en producción', EN_CAMINO: 'en camino', INCIDENCIA: 'incidencia' };

function saludo(): string {
  const h = new Date().getHours();
  return h < 13 ? 'Buenos días' : h < 20 ? 'Buenas tardes' : 'Buenas noches';
}

export function Inicio() {
  const { user } = useAuth();
  const { cliente, base } = useClienteActivo();
  const q = useQuery({ queryKey: ['inicio', cliente], queryFn: () => api.get<Inicio>(`/inicio?cliente=${encodeURIComponent(cliente)}`), staleTime: 60_000 });
  const d = q.data;
  const nombre = user?.email?.split('@')[0] ?? '';
  const hoy = fmt.date(new Date());
  const pk = `productos-${cliente}_f_`;

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-bold text-slate-900">{saludo()}, {nombre}</h1>
        <span className="text-sm text-slate-500">{hoy} · {cliente}{d ? ` · ${fmt.int(d.catalogo.vigentes)} productos vigentes` : ''}</span>
      </div>

      {q.isLoading && <Spinner />}
      {q.error && <p role="alert" className="rounded-md bg-bad-50 px-3 py-2 text-sm text-bad-700">No se pudo cargar el inicio. {(q.error as Error).message}</p>}

      {d && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Cue wide alerta={d.cues.rotura.n > 0} to={`${base}/productos?${pk}riesgo_rotura=${d.cues.rotura.umbral}`} label="Peligro de rotura de stock"
              valor={<>{fmt.int(d.cues.rotura.n)} <span className="text-base font-semibold">productos</span></>}
              nota={`menos de ${d.cues.rotura.umbral} ${d.cues.rotura.unidad} de cobertura${d.cues.rotura.sin_stock ? ` · ${fmt.int(d.cues.rotura.sin_stock)} sin stock con venta` : ''}`} />
            <Cue wide to={`${base}/pedidos`} label="Pedidos abiertos"
              valor={<>{fmt.int(d.cues.pedidos.abiertos)} <span className="text-base font-semibold">· {fmt.eur(d.cues.pedidos.importe)}</span></>}
              nota={d.cues.pedidos.por_estado.length ? d.cues.pedidos.por_estado.map((e) => `${e.n} ${ESTADO_LABEL[e.estado] ?? e.estado.toLowerCase()}`).join(' · ') : 'ninguno abierto'} />
            <Cue to={`${base}/cotizaciones`} label="Cotizaciones sin respuesta" valor={fmt.int(d.cues.cotizaciones.sin_respuesta)} alerta={d.cues.cotizaciones.dias_mas_antigua != null && d.cues.cotizaciones.dias_mas_antigua > 14}
              nota={d.cues.cotizaciones.mas_antigua ? `la más antigua, hace ${d.cues.cotizaciones.dias_mas_antigua} días` : d.cues.cotizaciones.sin_enviar ? `${d.cues.cotizaciones.sin_enviar} pendientes de enviar` : 'al día'} />
            <Cue to={`${base}/productos?${pk}revision_pendiente=true`} label="Fichas por revisar" valor={fmt.int(d.cues.revisar)} nota={d.cues.revisar ? 'marcadas «revisión pendiente»' : 'ninguna'} alerta={d.cues.revisar > 0} />
            <Cue to={`${base}/importaciones`} label="Histórico ventas APPROX" alerta={d.cues.ventas_approx.dias == null || d.cues.ventas_approx.dias > d.cues.ventas_approx.aviso_dias}
              valor={d.cues.ventas_approx.dias != null ? `${fmt.int(d.cues.ventas_approx.dias)} d` : '—'}
              nota={d.cues.ventas_approx.hasta ? `hasta el ${fmt.date(d.cues.ventas_approx.hasta)}${d.cues.ventas_approx.dias != null && d.cues.ventas_approx.dias > d.cues.ventas_approx.aviso_dias ? ' · pedir a APPROX' : ''}` : 'sin histórico cargado'} />
            {d.cues.coste_entrada ? (
              <Cue to={`${base}/importaciones`} label="Coste de entrada" alerta={d.cues.coste_entrada.dias == null || d.cues.coste_entrada.dias > d.cues.coste_entrada.aviso_dias}
                valor={d.cues.coste_entrada.dias != null ? `${fmt.int(d.cues.coste_entrada.dias)} d` : '—'}
                nota={d.cues.coste_entrada.hasta ? `último informe ${fmt.date(d.cues.coste_entrada.hasta)}` : 'sin informe cargado'} />
            ) : (
              <Cue to={`${base}/importador`} label="Stock actualizado" alerta={d.cues.stock.dias == null || d.cues.stock.dias > 45}
                valor={d.cues.stock.hasta ? fmt.date(d.cues.stock.hasta).slice(0, 5) : '—'}
                nota={d.cues.stock.dias != null ? `hace ${fmt.int(d.cues.stock.dias)} días` : 'sin extractos de stock'} />
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3"><h2 className="text-sm font-bold">Actividad reciente</h2><span className="text-xs text-slate-500">últimos movimientos</span></header>
              <ul className="divide-y divide-slate-100 px-5">
                {d.actividad.map((a, i) => {
                  const to = a.tipo === 'cotizacion' && a.to ? `${base}/cotizaciones/${a.to}` : a.tipo === 'pedido' && a.to ? `${base}/pedidos/${a.to}` : a.tipo === 'ficha' && a.to ? `${base}/productos/${a.to}` : `${base}/importaciones`;
                  const tono = a.tipo === 'importacion' ? 'good' : a.tipo === 'cotizacion' ? 'accent' : a.tipo === 'pedido' ? 'warn' : 'neutral';
                  const etiqueta = a.tipo === 'importacion' ? 'Importado' : a.tipo === 'cotizacion' ? 'Cotización' : a.tipo === 'pedido' ? 'Pedido' : 'Ficha';
                  return (
                    <li key={i} className="flex items-start gap-3 py-2 text-sm">
                      <Badge tone={tono} dot className="mt-0.5 shrink-0">{etiqueta}</Badge>
                      <Link to={to} className="min-w-0 flex-1 hover:underline"><span className="block truncate text-slate-800">{a.titulo}</span>{a.detalle && <span className="block truncate text-xs text-slate-500">{a.detalle}</span>}</Link>
                      <span className="shrink-0 text-xs text-slate-400">{fmt.datetime(a.fecha)}</span>
                    </li>
                  );
                })}
                {d.actividad.length === 0 && <li className="py-6 text-center text-sm text-slate-400">Sin actividad todavía.</li>}
              </ul>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white shadow-sm">
              <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3"><h2 className="text-sm font-bold">Ventas {d.serie.unidad === 'mes' ? 'mensuales' : 'semanales'} {cliente}</h2><span className="text-xs text-slate-500">unidades · últimos 12 {d.serie.unidad === 'mes' ? 'meses' : 'semanas'}</span></header>
              <div className="px-5 py-4">
                {d.serie.puntos.length > 1 ? <Tendencia puntos={d.serie.puntos} unidad={d.serie.unidad} /> : <p className="text-sm text-slate-400">Sin ventas importadas.</p>}
                {d.top.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Top del último mes ({d.top[0].periodo.slice(5, 7)}/{d.top[0].periodo.slice(0, 4)})</div>
                    <ol className="mt-1 space-y-1 text-sm">
                      {d.top.map((t, i) => <li key={t.id} className="flex items-center gap-2"><span className="w-4 text-right text-xs text-slate-400">{i + 1}</span><Link to={`${base}/productos/${t.id}`} className="min-w-0 flex-1 truncate hover:underline"><span className="font-mono text-xs text-slate-500">{t.sku}</span> {t.nombre}</Link><span className="font-semibold tabular-nums">{fmt.int(t.unidades)}</span></li>)}
                    </ol>
                  </div>
                )}
              </div>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

function Cue({ label, valor, nota, to, alerta, wide }: { label: string; valor: React.ReactNode; nota?: string; to: string; alerta?: boolean; wide?: boolean }) {
  return (
    <Link to={to} className={`relative block rounded-lg border bg-white px-4 py-3 shadow-sm transition hover:border-accent-300 hover:bg-accent-50/40 ${alerta ? 'border-warn-300' : 'border-slate-200'} ${wide ? 'col-span-2' : ''}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-extrabold leading-tight ${alerta ? 'text-warn-600' : 'text-slate-900'}`}>{valor}</div>
      {nota && <div className="mt-1 truncate text-xs text-slate-500" title={nota}>{nota}</div>}
      <span aria-hidden="true" className="absolute right-3 top-3 text-xs text-slate-400">↗</span>
    </Link>
  );
}

function Tendencia({ puntos, unidad }: { puntos: Inicio['serie']['puntos']; unidad: 'mes' | 'semana' }) {
  const W = 600; const H = 70; const pad = 4;
  const max = Math.max(1, ...puntos.map((p) => p.unidades));
  const y = (n: number) => H - pad - (n / max) * (H - pad * 2);
  const x = (i: number) => pad + (i / Math.max(1, puntos.length - 1)) * (W - pad * 2);
  const linea = puntos.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.unidades).toFixed(1)}`).join(' ');
  const ultimo = puntos[puntos.length - 1]; const previo = puntos[puntos.length - 2];
  const delta = previo && previo.unidades > 0 ? ((ultimo.unidades - previo.unidades) / previo.unidades) * 100 : null;
  const etiqueta = (v: string) => (unidad === 'mes' ? `${v.slice(5, 7)}/${v.slice(2, 4)}` : fmt.date(v));
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-16 w-full" role="img" aria-label="Tendencia de ventas">
        <path d={`${linea} L${x(puntos.length - 1).toFixed(1)},${H} L${pad},${H} Z`} fill="#2456B8" fillOpacity="0.1" />
        <path d={linea} fill="none" stroke="#2456B8" strokeWidth="2" vectorEffect="non-scaling-stroke" />
        <circle cx={x(puntos.length - 1)} cy={y(ultimo.unidades)} r="3" fill="#2456B8" />
      </svg>
      <div className="flex justify-between text-xs text-slate-500">
        <span>{etiqueta(puntos[0].periodo)}</span>
        <span><b className="text-slate-800">{etiqueta(ultimo.periodo)}: {fmt.int(ultimo.unidades)} uds</b>{ultimo.provisional ? ' (en curso)' : ''}{delta != null && <span className={delta >= 0 ? 'text-ok-700' : 'text-bad-600'}> · {delta >= 0 ? '+' : ''}{fmt.number(delta)} % vs anterior</span>}</span>
      </div>
    </div>
  );
}
